import React, { useEffect, useState } from 'react';
import { Drawer, Button, Grid, TextField, Chip, Box, Typography, CircularProgress, IconButton, Divider } from '@mui/material';
import { offerStandaloneManageService, type OfferStandaloneInquiryDTO } from '../../services/offerStandaloneManageService';

interface Props {
  open: boolean;
  onClose: () => void;
  inquiry: OfferStandaloneInquiryDTO | null;
  offerId: string;
  onUpdated?: () => void;
}

const statusChip = (status?: string) => {
  const map: Record<string, { bg: string; label: string }> = {
    'Przygotowane': { bg: '#E3F2FD', label: 'Przygotowane' },
    'draft gmail': { bg: '#FFF8E1', label: 'Draft Gmail' },
    'Wysłane': { bg: '#E8F5E9', label: 'Wysłane' }
  };
  const s = status ? (map[status] || { bg: '#f5f5f5', label: String(status) }) : { bg: '#f5f5f5', label: '-' };
  const textColor = '#020242';
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{
        bgcolor: s.bg,
        color: textColor,
        borderRadius: '4px',
        minHeight: 28,
        height: 28,
        '& .MuiChip-label': {
          px: 1,
          py: 0,
          fontFamily: 'Roboto, sans-serif',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '16px',
          color: textColor
        }
      }}
    />
  );
};

const OfferInquiryEmailDialog: React.FC<Props> = ({ open, onClose, inquiry, offerId, onUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [loadingBody, setLoadingBody] = useState(false);
  const [emailBody, setEmailBody] = useState<string>('');
  const [emailTo, setEmailTo] = useState<string>('');
  const [emailSubject, setEmailSubject] = useState<string>('');

  useEffect(() => {
    if (open && inquiry) {
      setLoadingBody(true);
      offerStandaloneManageService.getInquiryEmailBody(offerId, inquiry.inquiry_id)
        .then((data) => {
          console.log('Pobrana treść wiadomości:', data);
          setEmailBody(data.body || '');
          setEmailTo(data.to || '');
          setEmailSubject(data.subject || '');
        })
        .catch((err) => {
          console.error('Błąd pobierania treści wiadomości:', err);
          setEmailBody('');
          setEmailTo('');
          setEmailSubject('');
        })
        .finally(() => {
          setLoadingBody(false);
        });
    } else {
      setEmailBody('');
      setEmailTo('');
      setEmailSubject('');
    }
  }, [open, inquiry, offerId]);

  const handleCreateDraft = async () => {
    if (!inquiry) return;
    setLoading(true);
    try {
      await offerStandaloneManageService.createInquiryGmailDraft(offerId, inquiry.inquiry_id);
      onUpdated && onUpdated();
    } finally { setLoading(false); }
  };

  const handleSend = async () => {
    if (!inquiry) return;
    setLoading(true);
    try {
      if (inquiry.status === 'Przygotowane') {
        await offerStandaloneManageService.sendInquiryDirectEmail(offerId, inquiry.inquiry_id);
      } else if (inquiry.status === 'draft gmail') {
        await offerStandaloneManageService.sendInquiryGmailDraft(offerId, inquiry.inquiry_id, inquiry.gmail_draft_id);
      }
      onUpdated && onUpdated();
    } finally { setLoading(false); }
  };

  // Style pól formularza zgodne z AddPackageForm
  const textFieldSx = {
    '& .MuiInputLabel-root': {
      fontFamily: 'Roboto, sans-serif',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '16px',
      color: '#2D2D32'
    },
    '& .MuiOutlinedInput-root': {
      height: 32,
      backgroundColor: '#fff',
      borderRadius: '4px'
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
    '& .MuiInputBase-input': {
      padding: '8px 12px',
      boxSizing: 'border-box',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      color: '#2D2D32'
    },
    '& .MuiFormHelperText-root': { ml: 0 }
  } as const;

  const FieldLabel: React.FC<{ text: string }> = ({ text }) => (
    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32', mb: 0.5 }}>
      {text}
    </Typography>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: '50vw', maxWidth: '50vw', minWidth: 680, borderRadius: 0, top: '52px', height: 'calc(100% - 52px)' } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
            Wiadomość e‑mail
          </Typography>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 30,
              minHeight: 30,
              '& > *': {
                pointerEvents: 'none',
              },
            }}
          >
            <Box 
              component="img" 
              src="/assets/close.svg" 
              alt="Zamknij" 
              sx={{ 
                width: 13, 
                height: 13, 
                display: 'block',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }} 
            />
          </IconButton>
        </Box>
        <Box sx={{ p: 4, pt: 3, flex: 1, overflowY: 'auto' }}>
          <Grid container rowSpacing={1.5} columnSpacing={3}>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32' }}>
                Status:
              </Typography>
              {statusChip(inquiry?.status)}
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Do" />
              <TextField 
                fullWidth 
                size="small" 
                value={emailTo || inquiry?.contractor_email || ''} 
                InputProps={{ readOnly: true }}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Temat" />
              <TextField 
                fullWidth 
                size="small" 
                value={emailSubject || inquiry?.subject || ''} 
                InputProps={{ readOnly: true }}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Treść wiadomości" />
              {loadingBody ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : emailBody ? (
                <Box
                  sx={{
                    border: '1px solid #d4d4d9',
                    borderRadius: '4px',
                    p: 1.5,
                    minHeight: 200,
                    backgroundColor: '#fff',
                    maxHeight: 500,
                    overflow: 'auto',
                    '& table': {
                      width: '100%',
                      borderCollapse: 'collapse',
                    },
                    '& th, & td': {
                      padding: '8px',
                      border: '1px solid #dddddd',
                      textAlign: 'left',
                    },
                    '& th': {
                      backgroundColor: '#f2f2f2',
                      fontWeight: 'bold',
                    },
                    '& a': {
                      color: '#0563C1',
                      textDecoration: 'none',
                    },
                    '& a:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                  dangerouslySetInnerHTML={{ __html: emailBody }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center', fontFamily: 'Roboto, sans-serif' }}>
                  Brak treści wiadomości
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>
        <Divider />
        <Box sx={{ px: 4, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1, boxShadow: '0px 4px 4px rgba(0,0,0,0.05)' }}>
          <Button
            onClick={onClose}
            variant="text"
            disabled={loading || loadingBody}
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              textTransform: 'none',
              color: '#2D2D32',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            Zamknij
          </Button>
          {inquiry?.status === 'Przygotowane' && (
            <Button
              onClick={handleCreateDraft}
              variant="outlined"
              disabled={loading || loadingBody}
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                textTransform: 'none',
                borderColor: '#d4d4d9',
                color: '#2D2D32',
                '&:hover': {
                  borderColor: '#595966',
                  backgroundColor: 'transparent'
                }
              }}
            >
              Draft
            </Button>
          )}
          {(inquiry?.status === 'Przygotowane' || inquiry?.status === 'draft gmail') && (
            <Button
              onClick={handleSend}
              variant="contained"
              disabled={loading || loadingBody}
              sx={{
                backgroundColor: '#020242',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#030360'
                },
                '&:disabled': {
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)'
                }
              }}
            >
              Wyślij
            </Button>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default OfferInquiryEmailDialog;


